import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Modal, Spinner } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { formatInLanguage } from '@/utils/dateLocale';

type BranchEnrollModalProps = {
    isOpen: boolean;
    branchName: string;
    hasToken: boolean;
    /** Set only in the moment after this admin minted it. */
    revealedToken: string | null;
    /** Last four characters of the active token; null when unknown. */
    tokenPreview: string | null;
    setAt: string | null;
    isLoadingToken: boolean;
    copied: boolean;
    error: string | null;
    isGenerating: boolean;
    isRevoking: boolean;
    isCopying: boolean;
    onClose: () => void;
    onGenerate: () => void;
    onRevoke: () => void;
    onCopy: () => void;
    onCopyActive: () => void;
};

/**
 * Terminal-enrollment modal. Three states, in the order an admin meets them:
 *
 *   - no token → what a token is for, and Generate.
 *   - token just minted → the plaintext, a copy button, and the warning that
 *     this is its only appearance. The server keeps a hash and the last four
 *     characters, so nothing can show it again (BOS-118).
 *   - token already active → when it was set and which one it is (its last
 *     four), plus Rotate and Revoke.
 */
export const BranchEnrollModal: React.FC<BranchEnrollModalProps> = ({
    isOpen,
    branchName,
    hasToken,
    revealedToken,
    tokenPreview,
    setAt,
    isLoadingToken,
    copied,
    error,
    isGenerating,
    isRevoking,
    isCopying,
    onClose,
    onGenerate,
    onRevoke,
    onCopy,
    onCopyActive,
}) => {
    const { t, i18n } = useTranslation('pages');

    return (
        <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
            <Modal.Backdrop>
                <Modal.Container size="md">
                    <Modal.Dialog {...getNodeTestIdProp('branch-enroll')}>
                        <Modal.CloseTrigger />
                        <Modal.Header
                            {...getNodeTestIdProp('branch-enroll-header')}
                        >
                            <Modal.Heading
                                {...getNodeTestIdProp('branch-enroll-title')}
                            >
                                {t('branches.enroll.title', {
                                    branch: branchName,
                                })}
                            </Modal.Heading>
                            <p
                                className="text-sm text-muted"
                                {...getNodeTestIdProp(
                                    'branch-enroll-description',
                                )}
                            >
                                {t('branches.enroll.description')}
                            </p>
                        </Modal.Header>
                        <Modal.Body
                            className="flex flex-col gap-4"
                            {...getNodeTestIdProp('branch-enroll-body')}
                        >
                            {revealedToken !== null ? (
                                <div
                                    className="flex flex-col gap-3 rounded-xl border border-default bg-default-soft p-4"
                                    {...getNodeTestIdProp(
                                        'branch-enroll-reveal',
                                    )}
                                >
                                    <code
                                        className="block break-all rounded-lg bg-surface px-3 py-2 font-mono text-sm text-foreground"
                                        {...getNodeTestIdProp(
                                            'branch-enroll-token',
                                        )}
                                    >
                                        {revealedToken}
                                    </code>
                                    <p
                                        className="text-sm font-medium text-warning"
                                        {...getNodeTestIdProp(
                                            'branch-enroll-reveal-warning',
                                        )}
                                    >
                                        {t('branches.enroll.revealWarning')}
                                    </p>
                                    <Button
                                        onPress={onCopy}
                                        variant="secondary"
                                        {...getNodeTestIdProp(
                                            'branch-enroll-copy',
                                        )}
                                    >
                                        <Icon
                                            icon={
                                                copied
                                                    ? 'gravity-ui:check'
                                                    : 'gravity-ui:copy'
                                            }
                                        />
                                        {copied
                                            ? t('branches.enroll.copied')
                                            : t('branches.enroll.copy')}
                                    </Button>
                                </div>
                            ) : hasToken ? (
                                <div
                                    className="flex flex-col gap-2"
                                    {...getNodeTestIdProp(
                                        'branch-enroll-active',
                                    )}
                                >
                                    <p
                                        className="text-sm text-foreground"
                                        {...getNodeTestIdProp(
                                            'branch-enroll-status',
                                        )}
                                    >
                                        {t('branches.enroll.statusSet')}
                                    </p>
                                    {setAt !== null ? (
                                        <p
                                            className="text-sm text-muted"
                                            {...getNodeTestIdProp(
                                                'branch-enroll-set-at',
                                            )}
                                        >
                                            {t('branches.enroll.activeSince', {
                                                date: formatInLanguage(
                                                    setAt,
                                                    'D MMM YYYY',
                                                    i18n.language,
                                                ),
                                            })}
                                        </p>
                                    ) : null}
                                    {isLoadingToken ? (
                                        <span
                                            className="flex items-center gap-2 text-sm text-muted"
                                            {...getNodeTestIdProp(
                                                'branch-enroll-loading',
                                            )}
                                        >
                                            <Spinner
                                                aria-label={t(
                                                    'branches.enroll.loading',
                                                )}
                                                {...getNodeTestIdProp(
                                                    'branch-enroll-spinner',
                                                )}
                                            />
                                            {t('branches.enroll.loading')}
                                        </span>
                                    ) : tokenPreview !== null ? (
                                        <p
                                            className="font-mono text-sm text-muted"
                                            {...getNodeTestIdProp(
                                                'branch-enroll-preview',
                                            )}
                                        >
                                            {t('branches.enroll.endsIn', {
                                                preview: tokenPreview,
                                            })}
                                        </p>
                                    ) : (
                                        <p
                                            className="text-sm text-muted"
                                            {...getNodeTestIdProp(
                                                'branch-enroll-no-preview',
                                            )}
                                        >
                                            {t(
                                                'branches.enroll.statusNoPreview',
                                            )}
                                        </p>
                                    )}
                                    <Button
                                        className="self-start"
                                        isPending={isCopying}
                                        onPress={onCopyActive}
                                        size="sm"
                                        variant="secondary"
                                        {...getNodeTestIdProp(
                                            'branch-enroll-copy-active',
                                        )}
                                    >
                                        <Icon
                                            icon={
                                                copied
                                                    ? 'gravity-ui:check'
                                                    : 'gravity-ui:copy'
                                            }
                                        />
                                        {copied
                                            ? t('branches.enroll.copied')
                                            : t('branches.enroll.copy')}
                                    </Button>
                                </div>
                            ) : (
                                <p
                                    className="text-sm text-foreground"
                                    {...getNodeTestIdProp(
                                        'branch-enroll-status',
                                    )}
                                >
                                    {t('branches.enroll.statusUnset')}
                                </p>
                            )}

                            {error ? (
                                <p
                                    className="text-sm text-danger"
                                    role="alert"
                                    {...getNodeTestIdProp(
                                        'branch-enroll-error',
                                    )}
                                >
                                    {error}
                                </p>
                            ) : null}
                        </Modal.Body>
                        <Modal.Footer
                            {...getNodeTestIdProp('branch-enroll-footer')}
                        >
                            {/*
                             * While the freshly minted token is on screen the
                             * only sane next action is "I have copied it" —
                             * offering Rotate there would throw away the token
                             * the admin is halfway through copying, and it is
                             * the one thing nothing can show them again.
                             */}
                            {revealedToken !== null ? (
                                <Button
                                    onPress={onClose}
                                    variant="tertiary"
                                    {...getNodeTestIdProp('branch-enroll-done')}
                                >
                                    {t('branches.enroll.done')}
                                </Button>
                            ) : (
                                <>
                                    {hasToken ? (
                                        <Button
                                            isPending={isRevoking}
                                            onPress={onRevoke}
                                            variant="tertiary"
                                            {...getNodeTestIdProp(
                                                'branch-enroll-revoke',
                                            )}
                                        >
                                            {t('branches.enroll.revoke')}
                                        </Button>
                                    ) : null}
                                    <Button
                                        isPending={isGenerating}
                                        onPress={onGenerate}
                                        variant="primary"
                                        {...getNodeTestIdProp(
                                            'branch-enroll-generate',
                                        )}
                                    >
                                        {hasToken
                                            ? t('branches.enroll.rotate')
                                            : t('branches.enroll.generate')}
                                    </Button>
                                </>
                            )}
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
